import {
  CLINICIAN_ROLES,
  IAdvanceSearchFilter,
  IGroupInfo,
  SERVICE_STATUS,
} from "@home-health-notify/domain";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import {
  ADD_USER_TYPE,
  AddMembersContext,
  ApiErrorHandler,
  ChatInfoContext,
  IOrganizationContext,
  OrganizationContext,
  useEnrichGroupInfoAddress,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { useContext, useEffect, useRef, useState } from "react";
import AddMember from "../addMember/AddMember";
import { IPatientHeaderProps } from "../addMember/addUsers/advanceSearch/AdvanceSearchHelper";
import { AddGroupWrapper } from "../common/Common.style";
import { RightDrawerContext } from "../context/RightDrawerContext";
import {
  DrawerDivider,
  DrawerHeader,
  EmptyBox,
} from "../layout/RightDrawer.style";
import { AddPatientInfo } from "./AddPatientInfo";
import {
  getRequestData,
  ISelectedMembers,
  populateReferralFiles,
} from "./PatientGroupUtils";
import { ICreateOrEditPatientGroupRequest } from "@home-health-notify/models";
import { useNavigate } from "react-router";
import { ROUTES } from "../routes";
import config from "../utils/Config";
import {
  IPatientInformation,
  IRecentMRNLookup,
} from "@home-health-notify/domain/build/chat/emr/IEMRData";

const GOOGLE_MAPS_API_KEY = config.googleMapKey;

type AddPatientGroupProps = {
  mode?: "new" | "edit" | "episode";
  chatId?: string;
};

export const AddPatientGroup = ({
  mode = "new",
  chatId,
}: AddPatientGroupProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const doNothing = () => {};
  const tNsDrawer = (key: string) => t(key, { ns: "drawerHeader" });

  const { closeDrawer, openChatInfoDrawer } = useContext(RightDrawerContext);
  const { enqueueSnackbar } = useSnackbar();
  const { selectedUsers, setSelectedUsers, resetMembers } =
    useContext(AddMembersContext);

  const [showAddMembers, setShowAddMembers] = useState(false);
  const [patientGroupInfo, setPatientGroupInfo] = useState<IGroupInfo>(
    {} as IGroupInfo,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [patientInformation, setPatientInformation] = useState<
    IPatientInformation | undefined | null
  >();
  const [recentMRNs, setRecentMRNs] = useState<IRecentMRNLookup[]>([]);
  const [isRecentMRNsLoading, setIsRecentMRNsLoading] = useState(false);

  const { selectedOrgUser }: IOrganizationContext =
    useContext(OrganizationContext);
  const currentOrganization = selectedOrgUser?.getOrganization();
  const isEmrSyncSupported =
    !!currentOrganization?.organizationId &&
    !!config.emrReportingHost &&
    !!config.emrEnabledOrgs?.includes(currentOrganization.organizationId);

  const currentUser = currentOrganization?.userProfile ?? { id: "", title: "" };
  const isEdit = mode === "edit";
  const isEpisode = mode === "episode";

  // SPIKE: LLM extraction status drives the inline banner + form-blocking
  // backdrop. With regex-primary + LLM-fallback:
  //   - Regex succeeds (~10ms) → callback fires fast → status stays idle,
  //     UI never appears (see 200ms reveal delay below).
  //   - Regex fails → domain waits for LLM (2-7s) → callback fires late →
  //     UI has been revealed → banner flips to success/error.
  //
  // State transitions:
  //   idle → extracting     (on substantial paste, but UI reveal delayed 200ms)
  //   extracting → success  (data arrives; auto-clears in 2s)
  //   extracting → error    (error arrives; auto-clears in 4s)
  // Safety timer forces idle after 15s if callback never fires.
  type LlmStatus = "idle" | "extracting" | "success" | "error";
  const [llmStatus, setLlmStatus] = useState<LlmStatus>("idle");
  // showLlmUi gates the banner/backdrop separately from llmStatus.
  // It flips true only after 200ms of "extracting" so fast regex-succeed
  // paths (which finish in ~10ms) never render the UI.
  const [showLlmUi, setShowLlmUi] = useState<boolean>(false);
  // Refs mirror the state so the callback registered with
  // patientEmrParser (captured once on mount via useEffect) can read the
  // current value instead of a stale closure. Every state update must
  // update its ref alongside — use writeLlmStatus / writeShowLlmUi.
  const llmStatusRef = useRef<LlmStatus>("idle");
  const showLlmUiRef = useRef<boolean>(false);
  const writeLlmStatus = (status: LlmStatus) => {
    llmStatusRef.current = status;
    setLlmStatus(status);
  };
  const writeShowLlmUi = (show: boolean) => {
    showLlmUiRef.current = show;
    setShowLlmUi(show);
  };
  const llmAutoClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const llmSafetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const llmUiRevealRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearLlmTimers = () => {
    if (llmAutoClearRef.current) {
      clearTimeout(llmAutoClearRef.current);
      llmAutoClearRef.current = null;
    }
    if (llmSafetyTimeoutRef.current) {
      clearTimeout(llmSafetyTimeoutRef.current);
      llmSafetyTimeoutRef.current = null;
    }
    if (llmUiRevealRef.current) {
      clearTimeout(llmUiRevealRef.current);
      llmUiRevealRef.current = null;
    }
  };

  const { enrichAddress } = useEnrichGroupInfoAddress();

  useEffect(() => {
    if (mode !== "new" || !isEmrSyncSupported) return;
    const emrManager = selectedOrgUser?.emrManager;
    if (!emrManager) return;

    let isActive = true;

    const fetchRecentMRNs = async () => {
      setIsRecentMRNsLoading(true);
      try {
        const lookups = await emrManager.getRecentLookups();
        if (isActive) setRecentMRNs(lookups);
      } catch (e) {
        console.log(e);
      } finally {
        if (isActive) setIsRecentMRNsLoading(false);
      }
    };

    fetchRecentMRNs();

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgUser?.emrManager, isEmrSyncSupported, mode]);

  const handlePatientEmrParser = async (
    data: IGroupInfo | null,
    error: Error | null,
    pastedText?: string,
    targetElement?: HTMLInputElement | HTMLTextAreaElement,
  ) => {
    // SPIKE: update LLM banner status based on callback outcome.
    // Read llmStatusRef / showLlmUiRef (not the useState values) to avoid
    // stale-closure reads — this function was captured on mount and
    // doesn't re-register when state changes.
    if (llmStatusRef.current === "extracting") {
      const uiWasShown = showLlmUiRef.current;
      clearLlmTimers();
      if (error) {
        // On error, always show the outcome briefly so the user knows the
        // form is theirs to fill manually.
        writeShowLlmUi(true);
        writeLlmStatus("error");
        llmAutoClearRef.current = setTimeout(() => {
          writeLlmStatus("idle");
          writeShowLlmUi(false);
        }, 4000);
      } else if (data) {
        if (uiWasShown) {
          // Banner was visible during extraction — flip to success briefly.
          writeLlmStatus("success");
          llmAutoClearRef.current = setTimeout(() => {
            writeLlmStatus("idle");
            writeShowLlmUi(false);
          }, 2000);
        } else {
          // Fast path (regex succeeded) — never showed the banner, don't
          // flash a success message. Just clear state silently.
          writeLlmStatus("idle");
          writeShowLlmUi(false);
        }
      } else {
        writeLlmStatus("idle");
        writeShowLlmUi(false);
      }
    }

    if (error) {
      enqueueSnackbar(
        "Oops, data not parsed properly, pasting the content into the field.",
        { variant: "error" },
      );
      // The paste target is only a text field when focus was in an input /
      // textarea. When the paste happened elsewhere (a div, the body, etc.)
      // targetElement.value is undefined — guard so we don't crash splicing
      // text into a non-input. (WEB-APP-148: unhandled TypeError on .length)
      const isTextField =
        !!targetElement &&
        (targetElement.tagName === "INPUT" ||
          targetElement.tagName === "TEXTAREA") &&
        typeof targetElement.value === "string";
      if (pastedText && isTextField) {
        // Insert pasted text at cursor position, preserving existing content
        const existingValue = targetElement.value;
        const selectionStart =
          targetElement.selectionStart ?? existingValue.length;
        const selectionEnd = targetElement.selectionEnd ?? existingValue.length;
        const newValue =
          existingValue.substring(0, selectionStart) +
          pastedText +
          existingValue.substring(selectionEnd);

        // Set the merged value using native setters to trigger React
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          "value",
        )?.set;
        const nativeTextAreaValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLTextAreaElement.prototype,
          "value",
        )?.set;

        if (targetElement.tagName === "INPUT" && nativeInputValueSetter) {
          nativeInputValueSetter.call(targetElement, newValue);
        } else if (
          targetElement.tagName === "TEXTAREA" &&
          nativeTextAreaValueSetter
        ) {
          nativeTextAreaValueSetter.call(targetElement, newValue);
        }

        // Trigger both input and change events for React
        const inputEvent = new Event("input", { bubbles: true });
        const changeEvent = new Event("change", { bubbles: true });
        targetElement.dispatchEvent(inputEvent);
        targetElement.dispatchEvent(changeEvent);
      }
      return;
    }
    if (data) {
      const groupInfo = await enrichAddress(data, GOOGLE_MAPS_API_KEY);
      setPatientGroupInfo(groupInfo);
    }
  };

  useEffect(() => {
    if (!selectedOrgUser?.chatManager || isEdit) return;
    let unregisterEmrParser: () => void = () => {};

    const registerEmrParser = async () => {
      const result = selectedOrgUser.chatManager
        .getPatientChatManager()
        .registerEmrParser(handlePatientEmrParser);
      if (result) {
        unregisterEmrParser = result;
      }
    };

    registerEmrParser();

    return () => {
      unregisterEmrParser();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgUser?.chatManager]);

  // SPIKE: on a substantial paste, mark status as "extracting" AND arm a
  // 200ms timer to reveal the UI. If the callback fires within 200ms
  // (regex succeeded, ~10ms), the reveal timer is cancelled and the UI
  // never appears. If it fires later (LLM fallback path, 2-7s), the UI
  // has already appeared.
  //
  // The existing PatientEmrParser filters paste events for size (>500
  // chars OR >=10 newlines), so we mirror that check here to avoid noise
  // for small/unrelated pastes.
  useEffect(() => {
    if (isEdit) return;
    const onPaste = (e: ClipboardEvent) => {
      const text = e.clipboardData?.getData("Text") ?? "";
      const newlines = text.split("").filter(c => c === "\n").length;
      if (text.length < 500 && newlines < 10) return;
      clearLlmTimers();
      writeLlmStatus("extracting");
      writeShowLlmUi(false);
      // Delayed reveal — regex succeeds in ~10ms, so if we're still
      // "extracting" after 200ms we know we're on the LLM path and
      // should show the user what's happening.
      llmUiRevealRef.current = setTimeout(() => {
        writeShowLlmUi(true);
      }, 200);
      // Safety: if callback never fires (shouldn't happen — extractEmr
      // has an 8s internal timeout) force-clear after 15s.
      llmSafetyTimeoutRef.current = setTimeout(() => {
        writeLlmStatus("idle");
        writeShowLlmUi(false);
      }, 15000);
    };
    // Capture phase — this listener MUST fire before PatientEmrParser's
    // own bubble-phase paste listener, so `writeLlmStatus("extracting")`
    // completes before the parser's synchronous callback reads the ref.
    // Without capture, PatientEmrParser fires first, callback sees ref
    // still "idle", skips the "extracting → success/idle" transition, and
    // the banner appears 200ms later with no way to dismiss it. Verified:
    // Kinnser-in-Kinnser paste (regex succeeds) was showing a phantom
    // banner because of exactly this ordering.
    document.addEventListener("paste", onPaste, true);
    return () => {
      document.removeEventListener("paste", onPaste, true);
      clearLlmTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  const { chat } = useContext(ChatInfoContext);
  const grpinfo = chat?.chatInfo as IGroupInfo;
  const existingMembers = grpinfo?.members;

  useEffect(() => {
    if ((isEdit || isEpisode) && chat) {
      const groupInfo = { ...chat?.chatInfo } as IGroupInfo;
      setPatientGroupInfo(groupInfo);
      const captionMap = new Map<string, string>();
      groupInfo.attachments?.forEach(file => {
        captionMap.set(file.id, file.media[0].caption ?? "");
      });
      setFileCaption(captionMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat]);

  const addPatientInfo = (patientInfo: IGroupInfo) => {
    setPatientGroupInfo(patientInfo);
    if (isEdit) {
      addPatientGroup(undefined, patientInfo).then(() => {
        doNothing();
      });
    } else {
      if (isEpisode && existingMembers) {
        if (isEpisode && existingMembers && selectedUsers.length === 0) {
          const userDirectory =
            selectedOrgUser?.userDirectory.getFilteredActiveUserDirectory() ??
            [];
          const directoryIds = new Set(userDirectory.map(user => user.id));
          setSelectedUsers(
            existingMembers
              .filter(
                member =>
                  member.userId !== currentUser.id &&
                  directoryIds.has(member.userId),
              )
              .map(member =>
                userDirectory.find(user => user.id === member.userId),
              )
              .map(user =>
                user
                  ? {
                      type: ADD_USER_TYPE.USER,
                      id: user.id,
                      title: user.title,
                      refMsg: "",
                    }
                  : null,
              )
              .filter((u): u is Exclude<typeof u, null> => u !== null),
          );
        } else {
          setSelectedUsers(selectedUsers);
        }
      }

      setShowAddMembers(true);
    }
  };

  const resetForm = () => {
    setPatientGroupInfo({} as IGroupInfo);
    setShowAddMembers(false);
  };

  const fetchPatientDetails = async (mrn: string) => {
    const emrManager = selectedOrgUser?.emrManager;
    if (emrManager) {
      await emrManager?.fetchPatientInformation(mrn);
      setPatientInformation(emrManager?.patientInformation);
    }
  };

  const addPatientGroup = async (
    selectedMembers?: ISelectedMembers,
    patientInfo?: IGroupInfo,
  ) => {
    if (isEdit && !chatId) return;
    try {
      setIsLoading(true);
      const attachmentFiles = await populateReferralFiles(
        uploadFiles,
        patientGroupInfo,
        selectedOrgUser?.assetManager ?? ({} as any),
        fileCaptionMap,
      );
      const requestData: ICreateOrEditPatientGroupRequest = isEdit
        ? getRequestData(patientInfo, attachmentFiles)
        : getRequestData(patientGroupInfo, attachmentFiles, selectedMembers);
      // In edit mode the id must come from the chatId prop, never from form
      // state: submitting replaces patientGroupInfo with the id-less form
      // payload, so a retry after a failed update would drop the id and the
      // upsert endpoint would create a new group instead.
      const groupId = isEdit ? chatId : patientGroupInfo.id;
      const finalRequestData = groupId
        ? { ...requestData, id: groupId }
        : requestData;
      if (isEpisode) {
        const newEpisodeRequestData = { ...finalRequestData, id: chatId };
        await selectedOrgUser?.chatManager
          ?.getPatientChatManager()
          ?.createNewEpisode(newEpisodeRequestData);
        navigate(
          `${ROUTES.ORGANIZATIONS}/${currentOrganization?.organizationId}/${ROUTES.CHATS}`,
        );
        showMessage(requestData.name, tNsDrawer("createGroupSuccessMessage"));
      } else {
        await selectedOrgUser?.chatManager
          ?.getPatientChatManager()
          ?.addChat(finalRequestData);
        showMessage(
          requestData.name,
          isEdit
            ? tNsDrawer("editGroupSuccessMessage")
            : tNsDrawer("createGroupSuccessMessage"),
        );
      }
      if (!isEdit) {
        closeDrawer();
      } else {
        openChatInfo();
      }
      resetForm();
      resetMembers();
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const showMessage = (groupIdentifier: string, message: string) => {
    enqueueSnackbar(groupIdentifier + " " + message, {
      autoHideDuration: 3000,
    });
  };

  const openChatInfo = () => {
    openChatInfoDrawer();
    resetForm();
  };

  const getFileCaptions = () => {
    let captionMap = new Map();
    if (patientGroupInfo && patientGroupInfo.attachments) {
      patientGroupInfo.attachments.forEach(function (file) {
        captionMap.set(file.id, file.media[0].caption ?? "");
      });
    }
    return captionMap;
  };

  const [fileCaptionMap, setFileCaption] =
    useState<Map<string, string>>(getFileCaptions());
  const [uploadFiles, setUploadFiles] = useState<any[]>([]);

  const onRemoveFile = (fileName: string) => {
    if (patientGroupInfo) {
      patientGroupInfo.attachments = (
        patientGroupInfo.attachments || []
      ).filter(file => file.id !== fileName);
    }
    const updatedFiles = uploadFiles.filter(file => file.name !== fileName);
    setUploadFiles(updatedFiles);
  };

  const onSetCaption = (fileName: string, caption: string) => {
    setFileCaption(new Map(fileCaptionMap.set(fileName, caption)));
  };

  const getAdvanceSearchFilters = (): IAdvanceSearchFilter => {
    return {
      lat: patientGroupInfo?.patientInfo?.address?.lat ?? 0,
      lng: patientGroupInfo?.patientInfo?.address?.lng ?? 0,
    };
  };

  const getPatientHeaderProps = (): IPatientHeaderProps => {
    return {
      meta: patientGroupInfo?.meta,
      name: patientGroupInfo?.patientInfo?.name ?? "",
      patientStatus: patientGroupInfo?.patientStatus ?? "",
      servicesToPatient: [
        {
          id: "new-group-service-id",
          status: SERVICE_STATUS.PENDING,
          shorthand: (
            Object.keys(patientGroupInfo?.socStarter ?? {}) as CLINICIAN_ROLES[]
          ).find((property: CLINICIAN_ROLES) =>
            Boolean(
              (
                patientGroupInfo?.socStarter as unknown as Record<
                  string,
                  boolean
                >
              )?.[property],
            ),
          ) as CLINICIAN_ROLES,
        },
      ],
      address: patientGroupInfo?.patientInfo?.address ?? {
        geoLocation: { coordinates: [0, 0], type: "Point" },
        lat: 0,
        lng: 0,
        placeName: "",
        apartmentNo: "",
      },
    };
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const renderHeader = () => {
    switch (mode) {
      case "edit":
        return (
          <DrawerHeader>
            <IconButton
              data-testid="back-button"
              onClick={() => openChatInfo()}
            >
              <ChevronLeftIcon fontSize={"large"} />
            </IconButton>
            <Typography variant="h6">Edit Patient Group</Typography>
            <EmptyBox />
          </DrawerHeader>
        );

      case "episode":
        return (
          <DrawerHeader>
            {!showAddMembers ? (
              <EmptyBox />
            ) : (
              <IconButton onClick={() => setShowAddMembers(false)}>
                <ChevronLeftIcon fontSize={"large"} />
              </IconButton>
            )}
            <Typography variant="h6">
              {!showAddMembers
                ? tNsDrawer("newEpisode")
                : tNsDrawer("addMembers")}
            </Typography>
            <IconButton
              data-testid="close-button"
              onClick={() => {
                closeDrawer();
                setShowAddMembers(false);
                setPatientGroupInfo({} as IGroupInfo);
                resetMembers();
              }}
            >
              <CloseIcon />
            </IconButton>
          </DrawerHeader>
        );

      case "new":
      default:
        return (
          <DrawerHeader>
            {!showAddMembers ? (
              <EmptyBox />
            ) : (
              <IconButton onClick={() => setShowAddMembers(false)}>
                <ChevronLeftIcon fontSize={"large"} />
              </IconButton>
            )}
            <Typography variant="h6">
              {!showAddMembers
                ? tNsDrawer("newPatientGroup")
                : tNsDrawer("addMembers")}
            </Typography>
            <IconButton
              data-testid="close-button"
              onClick={() => {
                closeDrawer();
                setShowAddMembers(false);
                setPatientGroupInfo({} as IGroupInfo);
                resetMembers();
              }}
            >
              <CloseIcon />
            </IconButton>
          </DrawerHeader>
        );
    }
  };

  const renderContent = () => {
    switch (mode) {
      case "edit":
        return (
          <Box
            ref={containerRef}
            sx={{
              flexDirection: "column",
              overflow: "auto"
            }}>
            <DrawerDivider />
            {chat && (
              <AddPatientInfo
                addPatientInfo={addPatientInfo}
                patientGroupInfo={patientGroupInfo}
                uploadFiles={uploadFiles}
                setUploadFiles={setUploadFiles}
                fileCaptionMap={fileCaptionMap}
                onSetCaption={onSetCaption}
                onRemoveFile={onRemoveFile}
                isLoading={isLoading}
                mode={mode}
                ref={containerRef}
              />
            )}
          </Box>
        );

      case "episode":
        return showAddMembers ? (
          <AddMember
            callback={addPatientGroup}
            isLoading={isLoading}
            enableReferrals={true}
            advanceSearchFilters={getAdvanceSearchFilters()}
            patientHeaderProps={getPatientHeaderProps()}
          />
        ) : (
          <Box
            ref={containerRef}
            sx={{
              flexDirection: "column",
              overflow: "auto"
            }}>
            <DrawerDivider />
            {chat && (
              <AddPatientInfo
                addPatientInfo={addPatientInfo}
                patientGroupInfo={patientGroupInfo}
                uploadFiles={uploadFiles}
                setUploadFiles={setUploadFiles}
                fileCaptionMap={fileCaptionMap}
                onSetCaption={onSetCaption}
                onRemoveFile={onRemoveFile}
                isLoading={isLoading}
                mode={mode}
                ref={containerRef}
                expandFilledAccordion={true}
              />
            )}
          </Box>
        );

      case "new":
      default:
        return showAddMembers ? (
          <AddMember
            callback={addPatientGroup}
            isLoading={isLoading}
            enableReferrals
            advanceSearchFilters={getAdvanceSearchFilters()}
            patientHeaderProps={getPatientHeaderProps()}
          />
        ) : (
          <Box
            ref={containerRef}
            sx={{
              flexDirection: "column",
              overflow: "auto"
            }}>
            <DrawerDivider />
            {renderLlmStatusBanner()}
            <Box sx={{ position: "relative" }}>
              <AddPatientInfo
                patientInformation={patientInformation}
                fetchPatientDetails={fetchPatientDetails}
                addPatientInfo={addPatientInfo}
                patientGroupInfo={patientGroupInfo}
                uploadFiles={uploadFiles}
                setUploadFiles={setUploadFiles}
                fileCaptionMap={fileCaptionMap}
                onSetCaption={onSetCaption}
                onRemoveFile={onRemoveFile}
                isLoading={isLoading}
                mode={mode}
                ref={containerRef}
                expandFilledAccordion={true}
                isEmrSyncSupported={isEmrSyncSupported}
                recentMRNs={recentMRNs}
                recentMRNsLoading={isRecentMRNsLoading}
              />
              <Backdrop
                open={showLlmUi && llmStatus === "extracting"}
                sx={{
                  position: "absolute",
                  zIndex: theme => theme.zIndex.drawer + 1,
                  backgroundColor: "rgba(255, 255, 255, 0.65)",
                  color: "primary.main",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <CircularProgress />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Extracting patient info…
                </Typography>
              </Backdrop>
            </Box>
          </Box>
        );
    }
  };

  const renderLlmStatusBanner = () => {
    if (llmStatus === "idle" || !showLlmUi) return null;
    if (llmStatus === "extracting") {
      return (
        <Alert
          icon={<CircularProgress size={18} />}
          severity="info"
          sx={{ mx: 2, mt: 1 }}
        >
          Extracting patient info from your paste. This usually takes a few
          seconds.
        </Alert>
      );
    }
    if (llmStatus === "success") {
      // No success banner — the form populating is already visual
      // confirmation. Flashing an extra "success" toast on top is
      // redundant. Keep the "success" state around briefly (2s) only so
      // that if the callback re-fires, we don't accidentally trigger the
      // "extracting" state again from residual timers.
      return null;
    }
    // error
    return (
      <Alert severity="warning" sx={{ mx: 2, mt: 1 }}>
        Couldn’t fill in the form automatically. Please enter details manually.
      </Alert>
    );
  };

  return (
    <>
      <AddGroupWrapper>
        {renderHeader()}
        <Divider />
        {renderContent()}
      </AddGroupWrapper>
    </>
  );
};
export default AddPatientGroup;
